import { useEffect, useState } from "react";
import type { Team } from "@spikeeasy/tournament";
import { api } from "./api";
import { Notice, teamLabel } from "./ui";

type Code = { teamId: string; code: string | null };

export function useTeamCodes(tournamentId: string, enabled: boolean) {
  const [codes, setCodes] = useState<Code[] | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    setCodes(null);
    setError("");
    if (!enabled) return;
    let current = true;
    api<{ codes: Code[] }>(`/tournaments/${tournamentId}/codes`)
      .then((result) => {
        if (current) setCodes(result.codes);
      })
      .catch((e: Error) => {
        if (current) setError(e.message);
      });
    return () => {
      current = false;
    };
  }, [tournamentId, enabled, reload]);

  const generate = async (teamIds: string[]) => {
    setBusy(true);
    setError("");
    try {
      const result = await api<{ codes: Code[] }>(
        `/tournaments/${tournamentId}/codes`,
        { teamIds },
      );
      setCodes((previous) => [
        ...(previous ?? []).filter((row) => !teamIds.includes(row.teamId)),
        ...result.codes,
      ]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return { codes, error, busy, generate, retry: () => setReload((n) => n + 1) };
}

export function TeamCode({
  team,
  code,
  busy,
  replace,
}: {
  team: Team;
  code: string | null | undefined;
  busy: boolean;
  replace: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    setCopied(false);
    setError("");
  }, [code]);
  return (
    <div className="team-code">
      <small className="muted">Team access code</small>
      {code ? (
        <code>{code}</code>
      ) : (
        <span className="muted">Loading code…</span>
      )}
      <div className="actions">
        {code && (
          <button
            type="button"
            className="text-button"
            aria-label={`Copy code for ${teamLabel(team)}`}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(code);
                setCopied(true);
                setError("");
              } catch {
                setError(
                  "Could not copy. Select the code and copy it manually.",
                );
              }
            }}
          >
            {copied ? "Copied" : "Copy"}
          </button>
        )}
        <button
          type="button"
          className="text-button"
          disabled={busy || !code}
          aria-label={`Replace code for ${teamLabel(team)}`}
          onClick={() => {
            if (
              window.confirm(
                `Replace the access code for ${teamLabel(team)}? This signs the team out.`,
              )
            )
              replace();
          }}
        >
          Replace code
        </button>
      </div>
      {error && <Notice error>{error}</Notice>}
    </div>
  );
}
